import type { TrackingPixels, TrackingPixelsInput } from '../../lib/api/operatorConsole';

export interface TrackingPixelsForm {
  metaPixelId: string;
  redditPixelId: string;
  googleAdsConversionId: string;
  googleAdsPurchaseLabel: string;
  googleAdsCheckoutLabel: string;
  ga4MeasurementId: string;
  reportCurrency: string;
}

export type TrackingFieldName = keyof TrackingPixelsForm;
export type TrackingFormErrors = Partial<Record<TrackingFieldName, string>>;

// Mirrors the backend formats, so a typo shows up before the round-trip.
const FIELD_FORMATS: Record<TrackingFieldName, RegExp> = {
  metaPixelId: /^\d{10,20}$/,
  redditPixelId: /^[A-Za-z0-9_]{4,40}$/,
  googleAdsConversionId: /^AW-\d{6,15}$/,
  googleAdsPurchaseLabel: /^[A-Za-z0-9_-]{4,40}$/,
  googleAdsCheckoutLabel: /^[A-Za-z0-9_-]{4,40}$/,
  ga4MeasurementId: /^G-[A-Z0-9]{4,15}$/,
  reportCurrency: /^[A-Z]{3}$/,
};
const FIELD_NAMES = Object.keys(FIELD_FORMATS) as TrackingFieldName[]; // keys of a Record<TrackingFieldName, …>

// Three letters are not enough: a casino code like "GCS" would be reported as
// money. Browsers without supportedValuesOf keep the format check alone.
const KNOWN_CURRENCIES: ReadonlySet<string> | null =
  typeof Intl.supportedValuesOf === 'function' ? new Set(Intl.supportedValuesOf('currency')) : null;

export function toForm(config: TrackingPixels): TrackingPixelsForm {
  return {
    metaPixelId: config.meta?.pixel_id ?? '',
    redditPixelId: config.reddit?.pixel_id ?? '',
    googleAdsConversionId: config.google_ads?.conversion_id ?? '',
    googleAdsPurchaseLabel: config.google_ads?.purchase_label ?? '',
    googleAdsCheckoutLabel: config.google_ads?.checkout_label ?? '',
    ga4MeasurementId: config.ga4?.measurement_id ?? '',
    reportCurrency: config.report_currency ?? '',
  };
}

// Google shows the conversion id bare in the Tag Manager view ("123456789")
// and as "AW-123456789/label" in event snippets; both are accepted.
function googleAdsParts(form: TrackingPixelsForm): { conversionId: string; label: string } {
  const raw = form.googleAdsConversionId.trim();
  const slash = raw.indexOf('/');
  const id = slash === -1 ? raw : raw.slice(0, slash).trim();
  return {
    conversionId: /^\d+$/.test(id) ? `AW-${id}` : id,
    label: slash === -1 ? '' : raw.slice(slash + 1).trim(),
  };
}

function normalized(form: TrackingPixelsForm, field: TrackingFieldName): string {
  if (field === 'googleAdsConversionId') return googleAdsParts(form).conversionId;
  const text = form[field].trim();
  if (field === 'googleAdsPurchaseLabel') return text || googleAdsParts(form).label;
  return field === 'reportCurrency' ? text.toUpperCase() : text;
}

export function toPayload(form: TrackingPixelsForm): TrackingPixelsInput {
  const value = (field: TrackingFieldName) => normalized(form, field);
  const payload: TrackingPixelsInput = {};
  if (value('metaPixelId')) payload.meta = { pixel_id: value('metaPixelId') };
  if (value('redditPixelId')) payload.reddit = { pixel_id: value('redditPixelId') };
  if (value('googleAdsConversionId') || value('googleAdsPurchaseLabel') || value('googleAdsCheckoutLabel')) {
    payload.google_ads = {
      conversion_id: value('googleAdsConversionId'),
      purchase_label: value('googleAdsPurchaseLabel'),
      ...(value('googleAdsCheckoutLabel') ? { checkout_label: value('googleAdsCheckoutLabel') } : {}),
    };
  }
  if (value('ga4MeasurementId')) payload.ga4 = { measurement_id: value('ga4MeasurementId') };
  if (value('reportCurrency')) payload.report_currency = value('reportCurrency');
  return payload;
}

export function formatErrors(form: TrackingPixelsForm): TrackingFormErrors {
  const errors: TrackingFormErrors = {};
  for (const field of FIELD_NAMES) {
    const text = normalized(form, field);
    if (text && !FIELD_FORMATS[field].test(text)) errors[field] = 'Invalid format';
  }
  const hasConversionId = normalized(form, 'googleAdsConversionId') !== '';
  const hasLabel =
    normalized(form, 'googleAdsPurchaseLabel') !== '' || normalized(form, 'googleAdsCheckoutLabel') !== '';
  if (hasConversionId && !normalized(form, 'googleAdsPurchaseLabel')) {
    errors.googleAdsPurchaseLabel ??= 'Required with a conversion ID';
  }
  if (!hasConversionId && hasLabel) errors.googleAdsConversionId = 'Required with a conversion label';
  const currency = normalized(form, 'reportCurrency');
  if (currency && !errors.reportCurrency && KNOWN_CURRENCIES && !KNOWN_CURRENCIES.has(currency)) {
    errors.reportCurrency = 'Not an ISO 4217 currency';
  }
  return errors;
}

export function hasAnyProvider(form: TrackingPixelsForm): boolean {
  return Object.keys(toPayload(form)).some((key) => key !== 'report_currency');
}
